import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation as useRouterLocation } from 'react-router-dom'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { collection, getDocs, query, where } from 'firebase/firestore'
import { db } from '../firebase'
import { useLocation as useGeoLocation } from '../LocationContext'
import { logInstructorActivity, logInstructorLogin } from '../utils/instructorActivityLogger'

const ROUTE_LABELS = {
  '/dashboard': 'Dashboard',
  '/my-courses': 'My Courses',
  '/assignments': 'Assignments',
  '/workspace': 'Workspace',
  '/workshops': 'Workshops',
  '/assigned-profects': 'Client Projects',
  '/students-reports': 'Student Reports',
  '/my-thoughts': 'My Thoughts',
  '/ai-assistant': 'AI Assistant',
  '/profile': 'Profile',
  '/edit-profile': 'Edit Profile',
  '/class-session': 'Class Session',
  '/': 'Login',
  '/login': 'Login',
  '/recover': 'Password Recovery',
  '/forgot-password': 'Password Recovery',
}

function prettifyRoute(pathname = '') {
  if (ROUTE_LABELS[pathname]) return ROUTE_LABELS[pathname]

  if (pathname.startsWith('/student-profile/')) {
    return 'Student Profile'
  }

  return pathname
    .replace(/^\//, '')
    .split('/')
    .filter(Boolean)
    .map((segment) => segment.replace(/-/g, ' '))
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' / ') || 'Page View'
}

function InstructorActivityTracker() {
  const auth = getAuth()
  const routerLocation = useRouterLocation()
  const { location: geoLocation, currency } = useGeoLocation()
  const [activityProfile, setActivityProfile] = useState(null)
  const lastTrackedPathRef = useRef('')

  const geoDetails = useMemo(() => ({
    city: geoLocation?.city,
    region: geoLocation?.region,
    country: currency?.countryName || geoLocation?.country,
    timezone: geoLocation?.timezone,
  }), [currency?.countryName, geoLocation?.city, geoLocation?.region, geoLocation?.country, geoLocation?.timezone])

  useEffect(() => {
    let isMounted = true

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        if (isMounted) {
          setActivityProfile(null)
        }
        return
      }

      try {
        const employeeSnapshot = await getDocs(
          query(collection(db, 'Employees'), where('docId', '==', user.uid))
        )

        let employeeData = null
        employeeSnapshot.forEach((employeeDoc) => {
          employeeData = employeeDoc.data() || {}
        })

        const profile = {
          userId: user.uid,
          name: employeeData?.employeeName || user.displayName || 'Instructor',
          email: employeeData?.employeeEmail || user.email || '',
          jobPosition: employeeData?.jobPosition || 'Instructor',
        }

        if (isMounted) {
          setActivityProfile(profile)
        }

        await logInstructorLogin({
          userId: user.uid,
          profile,
          path: routerLocation.pathname,
          routeName: prettifyRoute(routerLocation.pathname),
          geo: geoDetails,
          metadata: {
            trigger: 'auth_state',
          },
        })
      } catch (error) {
        console.error('Unable to prepare instructor activity profile:', error)
      }
    })

    return () => {
      isMounted = false
      unsubscribe()
    }
  }, [auth, geoDetails, routerLocation.pathname])

  useEffect(() => {
    if (!activityProfile?.userId) return

    const routeKey = `${routerLocation.pathname}${routerLocation.search || ''}`
    if (lastTrackedPathRef.current === routeKey) return

    lastTrackedPathRef.current = routeKey

    logInstructorActivity({
      userId: activityProfile.userId,
      profile: activityProfile,
      eventType: 'page_view',
      path: routerLocation.pathname,
      routeName: prettifyRoute(routerLocation.pathname),
      geo: geoDetails,
      metadata: compactMetadata({
        search: routerLocation.search || undefined,
      }),
    })
  }, [activityProfile, geoDetails, routerLocation.pathname, routerLocation.search])

  return null
}

function compactMetadata(payload = {}) {
  return Object.entries(payload).reduce((accumulator, [key, value]) => {
    if (!value) return accumulator
    accumulator[key] = value
    return accumulator
  }, {})
}

export default InstructorActivityTracker

